import { useEffect, useRef, useState } from 'react';
import { uuidv7, type MealSlot } from '@st/contract';
import { imageContentTypes, isAllowedImageType, maxImageBytes } from '@st/contract';
import {
  useAddMeal,
  useMealTemplates,
  useUploadMealPhoto,
  type PhotoUploadStage,
} from '../../api/queries';
import { useTranslation } from '../../i18n/I18nProvider';
import { toErrorMessage } from '../../i18n/errorMessage';
import { cx } from '../../lib/cx';
import { formatNumber } from '../../lib/format';
import { MEAL_SLOT_KEY } from '../../lib/nutrition';
import { forWeight, type Food } from '@st/foods';
import { FoodPicker } from './FoodPicker';
import {
  Button,
  Chip,
  Field,
  GroupLabel,
  Modal,
  NumberInput,
  ProgressBar,
  Select,
  Textarea,
  borderClass,
  controlClass,
} from '../../ui';

const SLOTS: readonly MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack'];

/** Koraci prijenosa; iz njih se crta traka napretka. Isti niz kao kod napretka. */
const STAGES: readonly PhotoUploadStage[] = ['grant', 'upload', 'confirm'];

const STAGE_KEY: Record<PhotoUploadStage, string> = {
  grant: 'progress.stageGrant',
  upload: 'progress.stageUpload',
  confirm: 'progress.stageConfirm',
};

/** Isti rasponi koje `mealInput` provjerava na serveru — poruka stiže prije mreže. */
const KCAL_MAX = 20000;
const MACRO_MAX = 2000;

type MacroField = 'proteinG' | 'carbsG' | 'fatG';

const MACRO_FIELDS: readonly { field: MacroField; labelKey: string }[] = [
  { field: 'proteinG', labelKey: 'nutrition.protein' },
  { field: 'carbsG', labelKey: 'nutrition.carbs' },
  { field: 'fatG', labelKey: 'nutrition.fat' },
];

export interface MealModalProps {
  open: boolean;
  onClose: () => void;
  /** Dan koji je otvoren u dnevniku — obrok se upisuje na njega, ne uvijek na danas. */
  day: string;
  onSaved: () => void;
}

/**
 * Unos obroka.
 *
 * `clientIdempotencyKey` nastaje pri OTVARANJU obrasca, ne pri slanju (pravilo 10):
 * ako prvi pokušaj padne na mreži, ponovni klik na „Spremi" nosi isti ključ, pa
 * server prepozna već primljen obrok umjesto da upiše drugi.
 */
export function MealModal({ open, onClose, day, onSaved }: MealModalProps) {
  const { t, locale } = useTranslation();
  const addMeal = useAddMeal();
  const uploadPhoto = useUploadMealPhoto();
  const predlosci = useMealTemplates();

  const [slot, setSlot] = useState<MealSlot>('breakfast');
  const [description, setDescription] = useState('');
  const [kcal, setKcal] = useState<number | ''>('');
  const [macros, setMacros] = useState<Record<MacroField, number | ''>>({
    proteinG: '',
    carbsG: '',
    fatG: '',
  });
  const [errors, setErrors] = useState<Partial<Record<'description' | 'kcal' | MacroField, string>>>(
    {},
  );
  const [failure, setFailure] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [preview, setPreview] = useState<string | null>(null);
  const [stage, setStage] = useState<PhotoUploadStage | null>(null);

  /*
    Obrok se sprema PRIJE fotografije, jer ruta za dozvolu ide na
    `/v1/nutrition/meals/:id/photo-url`. Ako slika padne nakon toga, obrok je
    već na serveru i ne smije se izgubiti — zato se id pamti i ponovni pokušaj
    šalje samo sliku, na postojeći obrok.
  */
  const [savedMealId, setSavedMealId] = useState<string | null>(null);
  /** Ključ datoteke koja je već legla u R2 — ponovni pokušaj kreće od potvrde. */
  const uploadedKey = useRef<string | null>(null);

  const idempotencyKey = useRef<string>(uuidv7());

  // Novi obrazac = novi zapis = novi ključ. Zatvaranje i ponovno otvaranje ne smije
  // naslijediti ključ prethodnog obroka, inače bi drugi obrok bio odbijen kao duplikat.
  useEffect(() => {
    if (open) idempotencyKey.current = uuidv7();
  }, [open]);

  // Objekt-URL pregleda se oslobađa; inače svaka odabrana slika ostane u memoriji.
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function reset(): void {
    setSlot('breakfast');
    setDescription('');
    setKcal('');
    setMacros({ proteinG: '', carbsG: '', fatG: '' });
    setErrors({});
    setFailure(null);
    setFile(null);
    setFileError(undefined);
    setStage(null);
    setSavedMealId(null);
    uploadedKey.current = null;
  }

  const busy = addMeal.isPending || uploadPhoto.isPending;
  const mealSaved = savedMealId !== null;

  function close(): void {
    if (busy) return;
    reset();
    onClose();
  }

  /** Zatvaranje kad je obrok spremljen a slika pala — obrok ostaje, slika se odustaje. */
  function skipPhoto(): void {
    reset();
    onSaved();
    onClose();
  }

  function pick(chosen: File | null): void {
    // Nova datoteka poništava ključ prethodne — inače bi se potvrdila stara slika.
    uploadedKey.current = null;
    setFailure(null);
    setFile(chosen);

    if (!chosen) {
      setFileError(undefined);
      return;
    }
    if (!isAllowedImageType(chosen.type)) {
      setFileError(t('nutrition.photoTypeInvalid'));
      return;
    }
    if (chosen.size > maxImageBytes) {
      setFileError(
        t('nutrition.photoTooLarge', { max: formatNumber(maxImageBytes / (1024 * 1024), locale) }),
      );
      return;
    }
    setFileError(undefined);
  }

  /** Prazan obrazac i nemoguće vrijednosti ne putuju do servera. */
  function validate(): boolean {
    const next: typeof errors = {};

    /*
      Opis je jedino što obrok čini obrokom: bez njega trener vidi brojku bez
      sadržaja, a klijent poslije ne zna što je to bilo. Prije je prolazio i
      unos sa samim kalorijama — u aplikaciji nije, pa su web i telefon tražili
      različite stvari za isti zapis.
    */
    if (description.trim() === '') next.description = t('nutrition.descriptionRequired');

    if (kcal !== '' && (kcal < 0 || kcal > KCAL_MAX || !Number.isInteger(kcal))) {
      next.kcal = t('nutrition.kcalRange', { max: KCAL_MAX });
    }
    for (const { field } of MACRO_FIELDS) {
      const value = macros[field];
      if (value !== '' && (value < 0 || value > MACRO_MAX)) {
        next[field] = t('nutrition.macroRange', { max: MACRO_MAX });
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function save(): Promise<void> {
    if (fileError) return;
    // Kad je obrok već spremljen, ponovni klik šalje samo sliku — ne provjerava
    // se obrazac iznova i ne upisuje se drugi obrok.
    if (!savedMealId && !validate()) return;

    setFailure(null);
    try {
      /* Lokalna kopija: stanje se unutar iste asinkrone funkcije ne osvježava. */
      let mealId = savedMealId;
      if (!mealId) {
        const created = await addMeal.mutateAsync({
          clientIdempotencyKey: idempotencyKey.current,
          eatenOn: day,
          slot,
          description: description.trim() || null,
          kcal: kcal === '' ? null : kcal,
          proteinG: macros.proteinG === '' ? null : macros.proteinG,
          carbsG: macros.carbsG === '' ? null : macros.carbsG,
          fatG: macros.fatG === '' ? null : macros.fatG,
        });
        mealId = created.id;
        setSavedMealId(created.id);
      }

      if (file) {
        await uploadPhoto.mutateAsync({
          mealId,
          day,
          file,
          uploadedKey: uploadedKey.current,
          onStage: setStage,
          onUploaded: (key) => {
            uploadedKey.current = key;
          },
        });
      }

      reset();
      onSaved();
    } catch (err) {
      setFailure(toErrorMessage(t, err).title);
    } finally {
      setStage(null);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={t('nutrition.addMeal')}
      closeLabel={t('common.close')}
      footer={
        <>
          {/*
            Kad je obrok već spremljen a pala je samo slika, „Odustani" bi bilo
            netočno — obrok postoji. Zato tu stoji izlaz koji to i kaže.
          */}
          <Button variant="secondary" onClick={mealSaved ? skipPhoto : close} disabled={busy}>
            {mealSaved ? t('nutrition.photoSkip') : t('common.cancel')}
          </Button>
          <Button loading={busy} disabled={Boolean(fileError)} onClick={() => void save()}>
            {mealSaved ? t('nutrition.photoRetry') : failure ? t('common.retry') : t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {failure && (
          <div role="alert" className="flex flex-col gap-1 text-[13px] text-danger">
            <span>{failure}</span>
            {mealSaved && (
              <span className="text-ink-muted">{t('nutrition.photoSavedMealOnly')}</span>
            )}
          </div>
        )}

        <Select
          id="meal-slot"
          label={t('nutrition.slot')}
          value={slot}
          onChange={(e) => setSlot(e.target.value as MealSlot)}
          options={SLOTS.map((s) => ({ value: s, label: t(MEAL_SLOT_KEY[s]) }))}
        />


        {/*
          Opis je jedino obavezno polje, pa je jedino koje nosi oznaku. Prije su
          sva četiri odjeljka imala numerirano zaglavlje jednake težine — ekran
          je time dobio četiri naslova, a ne jednu jasnu obavezu.
        */}
        <Textarea
          id="meal-description"
          label={t('nutrition.description')}
          hint={t('nutrition.descriptionHint')}
          required
          rows={3}
          maxLength={1000}
          value={description}
          error={errors.description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <p className="text-[13px] leading-relaxed text-ink-muted">{t('nutrition.formLead')}</p>


        {/*
          Prijedlozi stoje ISPOD opisa jer ga popunjavaju. Iznad su izgledali
          kao zaseban zadatak, a zapravo su prečac do onoga što piše ispod njih.
        */}
        <div className="flex flex-col gap-3 rounded-lg bg-bg-alt p-3">
          <span className="text-[11.5px] uppercase tracking-[0.08em] text-ink-muted">
            {t('nutrition.quickTitle')}
          </span>
        {/*
            Trenerov jelovnik: brojke je odredio trener, pa klijent koji ih ne zna
            izračunati ne mora. Odabir POPUNI obrazac, ali ne sprema — porcija nije
            uvijek ista, a jedan dodir koji odmah spremi krivu porciju gori je od
            dva koja spreme točnu.
          */}
          {(predlosci.data?.items.length ?? 0) > 0 && (
            <div className="flex flex-col gap-2">
              <span className="text-[11.5px] uppercase tracking-[0.08em] text-ink-muted">
                {t('mealTemplates.pickTitle')}
              </span>
              <div className="flex flex-wrap gap-2">
                {predlosci.data?.items.map((p) => (
                  <Chip
                    key={p.id}
                    onClick={() => {
                      if (p.slot) setSlot(p.slot);
                      setDescription(p.note ? `${p.name} — ${p.note}` : p.name);
                      setKcal(p.kcal ?? '');
                      setMacros({
                        proteinG: p.proteinG ?? '',
                        carbsG: p.carbsG ?? '',
                        fatG: p.fatG ?? '',
                      });
                    }}
                  >
                    {p.kcal === null ? p.name : `${p.name} · ${p.kcal} kcal`}
                  </Chip>
                ))}
              </div>
              <span className="text-[12px] text-ink-muted">{t('mealTemplates.pickHelp')}</span>
            </div>
          )}

          {/*
            Namirnica se ZBRAJA, a predložak popunjava. Predložak je cijelo jelo,
            namirnica je sastojak — obrok je gotovo uvijek više njih.
          */}
          <FoodPicker
            onAdd={(food: Food, grams: number) => {
              const dio = forWeight(food, grams);
              const zaokruzi = (n: number) => Math.round(n * 10) / 10;
              setKcal((prije) => Math.round((prije === '' ? 0 : prije) + dio.kcal));
              setMacros((prije) => ({
                proteinG: zaokruzi((prije.proteinG === '' ? 0 : prije.proteinG) + dio.proteinG),
                carbsG: zaokruzi((prije.carbsG === '' ? 0 : prije.carbsG) + dio.carbsG),
                fatG: zaokruzi((prije.fatG === '' ? 0 : prije.fatG) + dio.fatG),
              }));
              const ime = locale === 'en' ? food.en : food.hr;
              const stavka = `${ime} ${grams} g`;
              setDescription((prije) => (prije.trim() ? `${prije.trim()}, ${stavka}` : stavka));
            }}
          />

        </div>

        <GroupLabel naslov={t('nutrition.stepHowMuch')} oznaka={t('nutrition.optional')} />
        <span className="text-[12.5px] text-ink-muted">{t('nutrition.howMuchHint')}</span>
        <NumberInput
          id="meal-kcal"
          label={t('nutrition.kcal')}
          suffix="kcal"
          min={0}
          max={KCAL_MAX}
          step={1}
          value={kcal}
          error={errors.kcal}
          onValueChange={setKcal}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {MACRO_FIELDS.map(({ field, labelKey }) => (
            <NumberInput
              key={field}
              id={`meal-${field}`}
              label={t(labelKey)}
              suffix="g"
              min={0}
              max={MACRO_MAX}
              step={0.1}
              value={macros[field]}
              error={errors[field]}
              onValueChange={(v) => setMacros((m) => ({ ...m, [field]: v }))}
            />
          ))}
        </div>


        <GroupLabel naslov={t('nutrition.stepPhoto')} oznaka={t('nutrition.optional')} />
        <Field
          id="meal-photo"
          label={t('nutrition.photo')}
          hint={t('nutrition.photoHint')}
          error={fileError}
        >
          <input
            id="meal-photo"
            type="file"
            accept={imageContentTypes.join(',')}
            /*
              Bez `capture`. S njim Android Chrome otvara kameru ODMAH i ne nudi
              galeriju — a obrok se često slika prije nego se stigne do telefona,
              pa je slika već u galeriji. Bez atributa sustav sam ponudi oboje.
            */
            aria-invalid={fileError ? true : undefined}
            aria-describedby={fileError ? 'meal-photo-error' : 'meal-photo-hint'}
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
            className={cx(
              controlClass,
              borderClass(!!fileError),
              /*
                Gumb unutar polja za datoteku bio je bez ruba i s vrlo blijedom
                ispunom — nista nije govorilo da je gumb. Sada izgleda kao
                `secondary`: isti rub (`line-control`, 4,09:1) i ista visina.
              */
              'file:mr-3 file:rounded-pill file:border file:border-line-control',
              'file:bg-surface file:px-4 file:py-1.5 hover:file:bg-bg-alt',
              'file:font-body file:text-[13px] file:font-medium file:text-ink file:cursor-pointer',
            )}
          />
        </Field>

        {preview && !fileError && (
          <img
            src={preview}
            alt={t('nutrition.photoPreview')}
            className="h-40 w-full rounded-md object-cover"
          />
        )}

        {/*
          Prijenos zna trajati; bez ovoga korisnik gleda gumb koji se vrti i ne
          zna je li slika uopće krenula. Koraci su stvarni, ne dekorativni.
        */}
        {uploadPhoto.isPending && stage && (
          <ProgressBar
            value={STAGES.indexOf(stage) + 1}
            max={STAGES.length}
            label={t(STAGE_KEY[stage])}
            valueLabel={`${STAGES.indexOf(stage) + 1}/${STAGES.length}`}
          />
        )}
        <span aria-live="polite" className="sr-only">
          {uploadPhoto.isPending && stage ? t(STAGE_KEY[stage]) : ''}
        </span>
      </div>
    </Modal>
  );
}
